import React, { useEffect, useMemo, useState } from 'react'
import { MagnifyingGlass, Trash, AddressBook, PingPong } from '@phosphor-icons/react'
import { Icon, IconButton, Input, Popover, PopoverContent, PopoverTrigger } from '@shopflo/ui'
import FormErrorText from 'src/components/common/FormErrorText'
import { useMerchant } from 'src/lib/contexts/MerchantContext'
import useSWR from 'swr'
import { apiURI } from 'src/lib/utils/constants'
import { fetcher, staticOptions } from 'src/lib/core/api-client'
import { truncateString } from 'src/lib/utils/helpers'
import {
  CustomerSegmentsProps,
  Segment,
  SegmentListResponse,
  SegmentUIOption
} from 'src/lib/types/common/customerConditions'

const CustomerSegments: React.FC<CustomerSegmentsProps> = ({ state, onChange, error }) => {
  const { segmentIds } = state
  const {
    getters: { merchant }
  } = useMerchant()

  const [searchTerm, setSearchTerm] = useState('')
  const [open, setOpen] = useState(false)

  const { data: fetchedSegments, isValidating } = useSWR<SegmentUIOption[], Error>(
    () => {
      return merchant?.accountId ? `/merchant/${merchant?.accountId}/segments?offset=1&limit=100` : null
    },
    async (url: string): Promise<SegmentUIOption[]> => {
      const res = await fetcher(url, apiURI.SEGMENTS_ENGINE)
      const data = (res as SegmentListResponse)?.data?.data || []
      return (
        data?.map((segment: Segment) => ({
          value: segment?.id,
          label: segment?.name,
          customerCount: segment?.metadata?.file_meta?.count || 0
        })) || []
      )
    },
    staticOptions
  )

  useEffect(() => {
    if (!fetchedSegments) return
    const validIds = fetchedSegments.map(s => s.value)
    const filteredIds = segmentIds.filter(id => validIds.includes(id))
    if (filteredIds.length !== segmentIds.length) {
      onChange({ ...state, segmentIds: filteredIds })
    }
  }, [fetchedSegments])

  const filteredSegments = useMemo<SegmentUIOption[]>(() => {
    return (
      fetchedSegments?.filter(
        (segment: SegmentUIOption) =>
          segment.label.toLowerCase().includes(searchTerm.toLowerCase()) && !segmentIds.includes(segment.value)
      ) || []
    )
  }, [fetchedSegments, searchTerm, segmentIds])

  const selectedSegments = useMemo<SegmentUIOption[]>(() => {
    return fetchedSegments?.filter((segment: SegmentUIOption) => segmentIds.includes(segment.value)) || []
  }, [fetchedSegments, segmentIds])

  const handleAddSegment = (id: string) => {
    onChange({ ...state, segmentIds: [...segmentIds, id] })
    setOpen(false)
    setSearchTerm('')
  }

  const handleRemoveSegment = (id: string) => {
    onChange({ ...state, segmentIds: segmentIds.filter((segmentId: string) => segmentId !== id) })
  }

  return (
    <div className='pb-2'>
      <div className='flex flex-col gap-3 pt-1 pb-4 border-b border-dotted border-zinc-200'>
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <div className='relative'>
              <Input
                id='choose-segment-input'
                placeholder='Choose segment'
                variant='filled'
                startAdornment={<MagnifyingGlass size={18} />}
                value={searchTerm}
                dimension='md'
                onChange={e => setSearchTerm(e.target.value)}
                onClick={() => setOpen(true)}
                className='cursor-pointer max-w-[600px]'
              />
            </div>
          </PopoverTrigger>
          <PopoverContent
            className='w-[--radix-popover-trigger-width] p-0 bg-white !border-none !rounded-xl overflow-hidden shadow-xl z-50'
            align='start'
            sideOffset={8}
          >
            <div className='flex flex-col max-h-[300px] overflow-y-auto p-1.5'>
              {filteredSegments.length > 0 ? (
                filteredSegments.map((segment: SegmentUIOption) => (
                  <button
                    id={`add-segment-${segment.value}`}
                    key={segment.value}
                    className='flex items-center justify-between px-3 py-2 hover:bg-zinc-50 rounded-md transition-colors w-full text-left'
                    onClick={() => handleAddSegment(segment.value)}
                  >
                    <div className='flex items-center gap-3'>
                      <Icon IconComponent={AddressBook} size='sm' className='text-zinc-400' />
                      <span className='text-text-sm-regular text-zinc-800'>{truncateString(segment.label, 40)}</span>
                    </div>
                  </button>
                ))
              ) : (
                <div className='p-4 text-center text-text-sm-regular text-zinc-400'>
                  {isValidating ? 'Loading segments...' : 'No segments found'}
                </div>
              )}
            </div>
          </PopoverContent>
        </Popover>

        {selectedSegments.length > 0 && (
          <div className='flex gap-2 w-full'>
            <div className='border-y-1 border-l-1 border-zinc-200 w-5 rounded-l-lg'></div>
            <div className='flex flex-col overflow-hidden w-full py-2'>
              {selectedSegments.map((segment: SegmentUIOption) => (
                <div key={segment.value} className='flex items-center justify-between py-1.5 '>
                  <div className='flex items-center gap-2'>
                    <Icon
                      IconComponent={PingPong}
                      size='sm'
                      className='text-zinc-400'
                      iconProps={{ weight: 'duotone' }}
                    />
                    <span className='text-text-sm-regular text-zinc-800'>{segment.label}</span>
                  </div>
                  <div className='flex items-center gap-2'>
                    <IconButton
                      id={`remove-segment-${segment.value}`}
                      IconComponent={Trash}
                      onClick={() => handleRemoveSegment(segment.value)}
                      size='xs'
                      variant='ghost'
                      className='text-zinc-500'
                    />
                  </div>
                </div>
              ))}
            </div>
            <div className='border-y-1 border-r-1 border-zinc-200 w-5 rounded-r-lg'></div>
          </div>
        )}
      </div>
      <FormErrorText message={error?.status ? error.message : ''} />
    </div>
  )
}

export default CustomerSegments
